import { useCallback } from "react"
import { postSubmit } from "@ui-seeker/utils"
import { useToast } from "@hooks"
import { BACKEND_APIS } from "@constants"
import {
	autoEuropeIsSubmitting,
	autoEuropeListData,
	clearAutoEuropeSignals,
} from "Pages/AutoEuropeTool/Page/@signals"

const extractId = o => (typeof o === 'object' && o !== null) ? o.value : o
const extractIds = arr => (arr ?? []).map(extractId)

const normalizeProvider = ({ providerId, providerName, value, type, direction, duration }) => ({
	providerId,
	...(providerName && { providerName }),
	value,
	...(type !== undefined && { type }),
	...(direction !== undefined && { direction }),
	...(duration && { duration }),
})

const transformData = (data) => ({
	...data,
	seasonSetId: data.seasonSetId ?? null,
	filterGroupOffices: extractIds(data.filterGroupOffices),
	filterGroupSites: extractIds(data.filterGroupSites),
	filterGroupWebSuppliers: extractIds(data.filterGroupWebSuppliers),
	filterGroupProviders: extractIds(data.filterGroupProviders),
	filterAcriss: extractIds(data.filterAcriss),
	filterCategories: extractIds(data.filterCategories),
	filterCategoriesExclusion: data.filterCategoriesExclusion ?? false,
	targetPosition: data.targetPosition ?? null,
	compareByCategory: data.compareByCategory ?? false,
	minimumMargin: data.minimumMargin ? {
		value: data.minimumMargin.value,
		providers: (data.minimumMargin.providers ?? []).map(normalizeProvider),
	} : data.minimumMargin,
	minimumProfit: data.minimumProfit ? {
		value: data.minimumProfit.value,
		providers: (data.minimumProfit.providers ?? []).map(normalizeProvider),
	} : data.minimumProfit,
	undercut: data.undercut ? {
		value: data.undercut.value,
		type: data.undercut.type,
		direction: data.undercut.direction ?? 0,
		providers: (data.undercut.providers ?? []).map(normalizeProvider),
	} : data.undercut,
	insurance: data.insurance ? {
		delta: Number(data.insurance.delta),
		profitPerDay: (data.insurance.profitPerDay ?? []).map(({ durationFrom, durationTo, min, max }) => ({
			durationFrom, durationTo, min: Number(min), max: Number(max),
		})),
		deltaByDuration: (data.insurance.deltaByDuration ?? []).map(({ durationFrom, durationTo, delta }) => ({
			durationFrom, durationTo, delta: Number(delta),
		})),
	} : null,
})

export default function useAutoEuropeFormSubmit({ getTableData }) {
	const { showToast } = useToast()

	const onSubmit = useCallback((data) => {
		const isEdit = !!data.id
		const url = `${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/${isEdit ? 'edit' : 'create'}`

		autoEuropeIsSubmitting.value = true

		postSubmit(url, transformData(data))
			.then(result => {
				autoEuropeIsSubmitting.value = false

				if (isEdit) {
					autoEuropeListData.value = autoEuropeListData.value.map(item =>
						item.id === result.id ? result : item
					)
				} else {
					getTableData()
				}

				clearAutoEuropeSignals()
				showToast.success(isEdit ? "Pricing tool updated" : "Pricing tool created")
			})
			.catch(async (e) => {
				autoEuropeIsSubmitting.value = false
				if (e?.status === 409) {
					const body = await e.json().catch(() => ({}))
					showToast.error(body?.error_msg || "Error saving AutoEurope pricing tool", { autoClose: 20000 })
				} else {
					showToast.error("Error saving AutoEurope pricing tool")
				}
			})
	}, [getTableData])

	return { onSubmit }
}